import { z } from 'zod';

export const clientSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1, 'Client name is required'),
  email: z.string().email().optional(),
  password: z.string().optional(),
  smallLogoUrl: z.string().url().optional(),
  largeLogoUrl: z.string().url().optional(),
  gitHubToken: z.string().min(1).optional(),
  gitHubRepoName: z.string().min(1).optional(),
  gitHubRepoOwner: z.string().min(1).optional(),
  gitHubBranch: z.string().min(1).default('main').optional()
});

export const clientAdminSchema = z.object({
  id: z.number().int().positive(),
  clientId: z.number().int().positive(),
  email: z.string().email()
});

export const clientAdminCreateSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address (for example name@company.com)')
});

export const clientSchemaExtended = clientSchema.extend({
  licenses: z.array(z.lazy(() => licenseSchema)).optional(),
  oauthApps: z.array(z.lazy(() => oauthAppSchema)).optional(),
  workfrontConfigs: z.array(z.lazy(() => workfrontConfigSchema)).optional(),
  fusionConfigs: z.array(z.lazy(() => fusionConfigSchema)).optional(),
  clientAdmins: z.array(z.lazy(() => clientAdminSchema)).optional()
});

export const fusionConfigSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1, 'FusionConfig name is required'),
  url: z.string().url('Invalid URL'),
  apiKey: z.string().min(1, 'API key is required'),
  clientId: z.number().int().optional()
});

export const fusionConfigSchemaExtended = fusionConfigSchema.extend({
  client: z.lazy(() => clientSchema).optional()
});

export const oauthAppSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1, 'OauthApp name is required'),
  clientAppId: z.string().min(1, 'Client ID is required'),
  clientAppSecret: z.string().min(1, 'Client Secret is required'),
  customerId: z.string().optional(),
  userId: z.string().optional(),
  privateKey: z.string().optional(),
  clientId: z.number().int().positive().optional()
});

export const oauthAppSchemaExtended = oauthAppSchema.extend({
  client: z.lazy(() => clientSchema).optional(),
  jwtWorkfrontConfig: z.array(z.lazy(() => workfrontConfigSchema)).optional(),
  webWorkfrontConfig: z.array(z.lazy(() => workfrontConfigSchema)).optional()
});

export const workfrontConfigSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1, 'WorkfrontConfig name is required'),
  url: z.string().url('Invalid URL'),
  apiKey: z.string().min(1, 'API key is required'),
  jwtOauthAppId: z.number().int().optional(),
  webOauthAppId: z.number().int().optional(),
  clientId: z.number().int().optional()
});

export const workfrontConfigSchemaExtended = workfrontConfigSchema.extend({
  jwtOauthApp: z.lazy(() => oauthAppSchema).optional(),
  webOauthApp: z.lazy(() => oauthAppSchema).optional(),
  licenses: z.array(z.lazy(() => licenseSchema)).optional(),
  objectDetectionlicenses: z.array(z.lazy(() => licenseSchema)).optional(),
  client: z.lazy(() => clientSchema).optional(),
  licenseSecondaryWorkfrontConfigs: z.array(z.lazy(() => licenseSecondaryWorkfrontConfigSchemaExtended)).optional()
});

export const licenseSchema = z.object({
  id: z.number().int().positive(),
  type: z.string().min(3, 'License type is required'),
  primary: z.boolean().default(false),
  fusionOrgName: z.string().min(3, 'Fusion Org Name must be at leasts 3 characters long').optional(),
  fusionOrgId: z.number().int().positive().optional(),
  fusionTeamName: z.string().optional(),
  fusionTeamId: z.number().int().positive().nullable().optional(),
  fusionTeamIds: z.number().array().default([]).optional(),
  fusionScenarioName: z.string().optional(),
  fusionScenarioId: z.number().int().positive().optional(),
  fusionFolderId: z.string().optional(),
  fusionScenarioFolderId: z.number().int().positive().optional(),
  aiSummaryEnabled: z.boolean().default(false),
  logMetricsEnabled: z.boolean().default(false),
  workfrontBackupEnabled: z.boolean().default(false),
  scenarioCompareEnabled: z.boolean().default(true),
  scenarioPromotionEnabled: z.boolean().default(false),
  workfrontPortfolioId: z.string().min(3, 'Workfront Portfolio ID is required'),
  workfrontProgramId: z.string().min(3, 'Workfront Program ID is required'),
  workfrontTemplateId: z.string().optional(),
  clientId: z.number().int().positive('Client ID must be a positive integer'),
  fusionConfigId: z.number().int().positive().optional(),
  workfrontConfigId: z.number().int().positive().optional(),
  workfrontReuseBackupConfig: z.boolean().default(true),
  workfrontObjectDetectionConfigId: z.number().int().positive().optional(),
  enabled: z.boolean().default(false),
  fusionImsToken: z.string().optional(),
  mainMetricId: z.number().int().positive().optional(),
  githubBackupEnabled: z.boolean().default(false)
});

export const licenseEditSchema = licenseSchema.extend({
  licenseSecondaryWorkfrontConfigIds: z.number().array().default([]).optional()
});

export const licenseSchemaExtended = licenseSchema.extend({
  client: z.lazy(() => clientSchema).optional(),
  fusionConfig: z.lazy(() => fusionConfigSchema).optional(),
  workfrontConfig: z.lazy(() => workfrontConfigSchema).optional(),
  workfrontObjectDetectionConfig: z.lazy(() => workfrontConfigSchema).optional(),
  licenseSecondaryWorkfrontConfigs: z.array(z.lazy(() => licenseSecondaryWorkfrontConfigSchemaExtended)).optional()
});

export const licenseSecondaryWorkfrontConfigSchema = z.object({
  licenseId: z.number().int().positive(),
  workfrontConfigId: z.number().int().positive()
});

export const licenseSecondaryWorkfrontConfigSchemaLisenseExtended = licenseSecondaryWorkfrontConfigSchema.extend({
  lisense: z.lazy(() => licenseSchema).optional()
});

export const licenseSecondaryWorkfrontConfigSchemaWorkfrontConfigExtended =
  licenseSecondaryWorkfrontConfigSchema.extend({
    workfrontConfig: z.lazy(() => workfrontConfigSchema).optional()
  });

export const licenseSecondaryWorkfrontConfigSchemaExtended = licenseSecondaryWorkfrontConfigSchemaLisenseExtended.merge(
  licenseSecondaryWorkfrontConfigSchema
);

// Types inferred from the Zod schemas
export type Client = z.infer<typeof clientSchema>;
export type ClientAdmin = z.infer<typeof clientAdminSchema>;
export type FusionConfig = z.infer<typeof fusionConfigSchema>;
export type License = z.infer<typeof licenseSchema>;
export type LicenseSecondaryWorkfrontConfig = z.infer<typeof licenseSecondaryWorkfrontConfigSchema>;
export type OauthApp = z.infer<typeof oauthAppSchema>;
export type WorkfrontConfig = z.infer<typeof workfrontConfigSchema>;

export type ClientExtended = z.infer<typeof clientSchemaExtended>;
export type FusionConfigExtended = z.infer<typeof fusionConfigSchemaExtended>;
export type LicenseExtended = z.infer<typeof licenseSchemaExtended>;
export type LicenseSecondaryWorkfrontConfigLisenseExtended = z.infer<
  typeof licenseSecondaryWorkfrontConfigSchemaLisenseExtended
>;
export type LicenseSecondaryWorkfrontConfigWorkfrontConfigExtended = z.infer<
  typeof licenseSecondaryWorkfrontConfigSchemaWorkfrontConfigExtended
>;
export type LicenseEdit = z.infer<typeof licenseEditSchema>;
export type LicenseSecondaryWorkfrontConfigExtended = z.infer<typeof licenseSecondaryWorkfrontConfigSchemaExtended>;
export type OauthAppExtended = z.infer<typeof oauthAppSchemaExtended>;
export type WorkfrontConfigExtended = z.infer<typeof workfrontConfigSchemaExtended>;
